// @givmo/app-charity — a module's own TEAM page, at `<module path>/team` (shell contract v3, T6.7).
//
// ══ ONE ROUTE, EVERY MOUNTED MODULE — A MODULE ADDS NOTHING TO GET IT ═══════════════════════════
//
// The shell declares an "Admin Team" row for every mounted module (OM-100); this is its page. One
// parameterized route (`$module/team`, the segment being the module's PATH) rather than one per
// module — exactly the per-module sign-in door's shape. An unknown or unmounted segment is
// not-found; a mounted one (Donations, today) renders behind the module's direct-link gate, so a
// Member without the module meets the same answer a deep link into it would.
//
// What the page holds depends on who is looking (`OrgTeamSections`; OM-96, D-CPO-77). Someone who
// may manage the whole team (`write_member_access` on a settled standing read: a Global Admin) gets
// Team access's sections IN PLACE, with this app's frame and sidebar kept: the whole team with this
// app's column, every invite with this app's column, every request and the connected AIs, and
// "Invite someone" opens the organization-wide invite with this app already switched on. Anyone else
// (an Owner of the app) gets the app's own view, in the ONE order every Team page in both consoles
// wears (D-CP-51 and its amendment: the people already on the team, then the people on
// their way in, then the people asking): the roster narrowed to the module with the per-app tier
// column; the module's view of the ONE invite surface (`OrgInvitesPanel` with `module=`: the live
// offers that include this app, server-scoped, for a holder of `<module>:invite_admins`; "Invite
// someone" opens the MODULE-ISSUED door — that one app at a level no higher than the issuer's own,
// never Global Admin, `issued_module` on the wire — for an Owner-tier holder of the string); then
// the module's view of the ONE request table (`OrgAccessRequestsPanel` with `module=`: that module's
// rows, the App column omitted — for a holder of `<module>:approve_access_requests`, the calm
// statement for anyone else). Neither view links to the Admin Console, except a request for an app
// that is switched off ("Turn on {app} first", where turning it on happens).
// Inviting someone into the app is one click from its own Team page.
//
// DATA CONTRACT: `page-<slug>-team` is the page's root; the sidebar row is `menu-<slug>-team`.

import { ModuleGate, OrgTeamSections, TEAM_COPY, moduleForSegment } from '@givmo/org-console';
import { testIdProp } from '@givmo/design-system';
import { NotFound } from '../../components/not-found';
import { PageShell } from '../../components/page-shell';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { charityModuleDefinition, charityModuleRoutes } from '../../console/module-table';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useTurnOnHref } from '../../console/use-turn-on-href';
import { useOrgConsoleConfig } from '../org-console/use-org-console-config';

export interface ModuleTeamPageProps {
  slug: string;
}

export function ModuleTeamPage({ slug }: ModuleTeamPageProps) {
  const config = useOrgConsoleConfig();
  return (
    <PageShell testId={`page-${slug}-team`} title={TEAM_COPY.adminTeam}>
      {config === null ? (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp('module-team-pending')}>
          Loading…
        </p>
      ) : (
        // OM-96: a Global Admin gets Team access's sections in place; anyone else, the app's own.
        <OrgTeamSections config={config} module={slug} />
      )}
    </PageShell>
  );
}

export interface ModuleTeamRouteProps {
  /** The path segment from the address bar. */
  segment: string;
}

/** The route's component: the segment's module behind its gate, or not-found. */
export function ModuleTeamRoute({ segment }: ModuleTeamRouteProps) {
  const route = moduleForSegment(segment, charityModuleRoutes());
  const { pending, errored, standings, refetch } = useCharityConsole();
  const door = useRequestAccessDoor();
  const goInApp = useModuleNavigate();
  const turnOnHref = useTurnOnHref(route?.slug ?? '');
  if (route === undefined) return <NotFound />;

  const definition = charityModuleDefinition(route.slug);
  const standing = standings?.[route.slug];
  const accessRequest =
    standing?.accessRequest?.status === 'PENDING'
      ? { status: 'pending' as const }
      : standing?.accessRequest?.status === 'DENIED'
        ? { status: 'declined' as const, note: standing.accessRequest.resolutionNote ?? null }
        : undefined;

  return (
    <ModuleGate
      slug={route.slug}
      read={{ pending, errored, standings }}
      label={definition?.label}
      turnOnHref={turnOnHref}
      onNavigate={goInApp}
      onRetry={refetch}
      // The request door is withheld on a module the person already holds (409 for a holder).
      onRequestAccess={standing?.held === true ? undefined : () => door.open(route.slug)}
      accessRequest={accessRequest}
      renderNotFound={() => <NotFound />}
    >
      <ModuleTeamPage slug={route.slug} />
    </ModuleGate>
  );
}
